import { Track } from "@/src/lib/tracking";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { ROUTER_PATH } from "@config/router";
import { getBridge } from "./bridge";
import { MessageType } from "./types";
import {
  getContainerRouteContext,
  isChatShellContainer,
  NATIVE_DETAIL_CONTAINER_ID,
} from "./containerRouteMeta";
import { normalizePathname } from "@utils/routeNormalizer";
import {
  shouldDisableDetailGestureBack,
  shouldDisableShellImmersiveBack,
} from "@utils/bridge/detailGestureBackPolicy";
import {
  isChatTabInShellPath,
  staysInMainTabShellWebView,
} from "@utils/bridge/shellTabPathPolicy";
import { isUnifiedNativeMainShell } from "./unifiedMainShell";

// Detail 二级页 history 索引，用于判断是否可退（Android/iOS 原生返回同步读取同一 key）
const DETAIL_HISTORY_INDEX_KEY = "__edgenDetailIdx";
let detailHistoryTrackerInstalled = false;

function readDetailHistoryIndex(state: unknown): number | null {
  if (!state || typeof state !== "object") {
    return null;
  }
  const value = (state as Record<string, unknown>)[DETAIL_HISTORY_INDEX_KEY];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

function writeDetailHistoryIndex(
  nextIndex: number,
  mode: "push" | "replace",
): void {
  if (typeof window === "undefined") {
    return;
  }
  const currentState =
    window.history.state && typeof window.history.state === "object"
      ? (window.history.state as Record<string, unknown>)
      : {};
  const nextState = {
    ...currentState,
    [DETAIL_HISTORY_INDEX_KEY]: Math.max(0, Math.floor(nextIndex)),
  };
  if (mode === "push") {
    window.history.pushState(nextState, "", window.location.href);
    return;
  }
  window.history.replaceState(nextState, "", window.location.href);
}

/** Detail WebView 首帧安装 history 深度追踪（open-detail 直开时亦生效）。 */
export function initNativeDetailShellHistoryTracking(): void {
  if (typeof window === "undefined" || !isInNativeDetailShell()) {
    return;
  }
  ensureDetailHistoryTracker();
}

function ensureDetailHistoryTracker(): void {
  if (typeof window === "undefined" || detailHistoryTrackerInstalled) {
    return;
  }
  detailHistoryTrackerInstalled = true;

  const historyObj = window.history;
  const rawPushState = historyObj.pushState.bind(historyObj);
  const rawReplaceState = historyObj.replaceState.bind(historyObj);

  historyObj.pushState = function patchedPushState(
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ): void {
    const baseIdx = readDetailHistoryIndex(window.history.state) ?? 0;
    const mergedData =
      data && typeof data === "object"
        ? {
            ...(data as Record<string, unknown>),
            [DETAIL_HISTORY_INDEX_KEY]: baseIdx + 1,
          }
        : { [DETAIL_HISTORY_INDEX_KEY]: baseIdx + 1 };
    rawPushState(mergedData, unused, url);
  };

  historyObj.replaceState = function patchedReplaceState(
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ): void {
    const currentIdx = readDetailHistoryIndex(window.history.state) ?? 0;
    const mergedData =
      data && typeof data === "object"
        ? {
            ...(data as Record<string, unknown>),
            [DETAIL_HISTORY_INDEX_KEY]: currentIdx,
          }
        : { [DETAIL_HISTORY_INDEX_KEY]: currentIdx };
    rawReplaceState(mergedData, unused, url);
  };

  // Detail 首帧基线：当前页记为 0，后续 push 才算可退深度。
  if (readDetailHistoryIndex(window.history.state) === null) {
    writeDetailHistoryIndex(0, "replace");
  }
}

function canGoBackInsideDetailWebView(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  ensureDetailHistoryTracker();
  const idx = readDetailHistoryIndex(window.history.state) ?? 0;
  return idx > 0;
}

/**
 * 从 href 解析出用于「是否留在四 Tab 壳」判断的规范化 pathname（不含 query/hash）。
 */
function pathnameForShellTabPolicyFromHref(href: string): string {
  const noHash = href.split("#")[0] ?? "";
  if (/^https?:\/\//i.test(noHash)) {
    try {
      const u = new URL(noHash);
      return normalizePathname(u.pathname);
    } catch {
      return "/";
    }
  }
  const q = noHash.indexOf("?");
  const pathOnly = q >= 0 ? noHash.slice(0, q) : noHash;
  return normalizePathname(pathOnly || "/");
}

/** 当前文档是否在 Chat 底部 modal 的常驻 WebView 内。 */
export function isInNativeChatShellModal(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (!getBridge().isInApp()) {
    return false;
  }
  if (isChatShellContainer()) {
    return true;
  }
  // Android 部分版本未注入 tabIndex/containerId 时，用路由兜底识别 Chat 壳 WebView
  return isChatTabInShellPath(normalizePathname(window.location.pathname));
}

/** 当前文档是否在原生 Detail 二级 WebView（与 iOS 注入 / Android query 对齐）。 */
export function isInNativeDetailShell(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return getContainerRouteContext().containerId === NATIVE_DETAIL_CONTAINER_ID;
}

/**
 * App 内、目标不在四 Tab 壳、且当前不在 Detail 时，应新开原生 Detail（open-detail）。
 * 已在 Detail 内跳非壳路径应走 `router.push`，留在同一二级 WebView。
 */
export function shouldUseNativeDetailForHref(href: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (!getBridge().isInApp()) {
    return false;
  }
  // 二级 WebView 内互跳：不再次 open-detail，由 navigateShellAware 走 router
  if (isInNativeDetailShell()) {
    return false;
  }
  // `https://edgen.*.edwealth.ai/blog/...` 等绝对链：走 open-url-inapp，勿 open-detail
  if (shouldOpenInAppBrowserForHref(href)) {
    return false;
  }
  return !staysInMainTabShellWebView(pathnameForShellTabPolicyFromHref(href));
}

/** App 内打开原生 Detail，并按目标路径同步手势返回策略。 */
export function openNativeDetailForHref(href: string): void {
  const targetPathname = pathnameForShellTabPolicyFromHref(href);
  getBridge().openDetailInNative(resolveAppNavigationUrl(href), {
    gestureBackEnabled: !shouldDisableDetailGestureBack(targetPathname),
  });
}

/** Detail 二级 → 四 Tab 壳路径：应 pop Detail 并在对应 Tab WebView 打开。 */
export function shouldPopDetailForTabShellNavigation(href: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (!getBridge().isInApp()) {
    return false;
  }
  if (!isInNativeDetailShell()) {
    return false;
  }
  return staysInMainTabShellWebView(pathnameForShellTabPolicyFromHref(href));
}

/**
 * App 内、目标为四 Tab 壳内路径，且归属 Tab 与当前 WebView 的 `tabIndex` 不一致（或未知）：
 * 应走 `popDetailAndNavigateShell`，由原生在对应 Tab WebView 加载，而非当前 Tab 内 `router.push`。
 *
 * 与 `shouldPopDetailForTabShellNavigation` 区分：后者仅处理「当前在 Detail 叠层 → 回壳」；
 * 本函数处理「当前在某个 Tab WebView（或 tabIndex 未注入）→ 跳到另一 Tab 的壳内路由」。
 */
export function shouldPopDetailForCrossTabShellNavigation(
  href: string,
): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (!getBridge().isInApp()) {
    return false;
  }
  const shellPathname = pathnameForShellTabPolicyFromHref(href);
  // 非壳路径不走本分支（非壳由 open-detail 或 Detail 内 router 处理）
  if (!staysInMainTabShellWebView(shellPathname)) {
    return false;
  }

  // 统一主壳：主 Tab 同 WebView SPA；仅主壳 ↔ Chat 需原生 handoff
  if (!isUnifiedNativeMainShell()) {
    return false;
  }
  if (isInNativeChatShellModal()) {
    return !isChatTabInShellPath(shellPathname);
  }
  return isChatTabInShellPath(shellPathname);
}

/** 与原生壳 `isEdgenSiteHost` 对齐：站内绝对链仍走壳感知导航，非 Edgen 域名为站外。 */
function isEdgenSiteHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  if (h === "localhost" || h.endsWith(".localhost") || h === "127.0.0.1") {
    return true;
  }
  return h.includes("edwealth.ai") || h.includes("edwealth.");
}

/** `https://` 且 host 为 Edgen 域（含 edgen.beta.edwealth.ai）。 */
function isEdgenAbsoluteNavigationHref(href: string): boolean {
  const trimmed = href.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return false;
  }
  try {
    return isEdgenSiteHost(new URL(trimmed).hostname);
  } catch {
    return false;
  }
}

/**
 * App 内绝对 Edgen 链且非四 Tab 壳（如 `config.externalLink.aiChat` + `/blog/...`）：
 * 与 Aura / ArticleCard 旧版 `openUrl()` 一致，走 `open-url-inapp`，不用 `open-detail`。
 */
export function shouldOpenInAppBrowserForHref(href: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (!getBridge().isInApp()) {
    return false;
  }
  if (!isEdgenAbsoluteNavigationHref(href)) {
    return false;
  }
  return !staysInMainTabShellWebView(pathnameForShellTabPolicyFromHref(href));
}

/**
 * 站外 / 特殊 scheme（mailto、tel 等）：不走 open-detail / pop-detail，由 ShellLink 渲染 `<a>`。
 */
export function isOffSiteNavigationHref(href: string): boolean {
  const trimmed = href.trim();
  if (!trimmed) {
    return false;
  }
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("mailto:") || lower.startsWith("tel:")) {
    return true;
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return false;
  }
  try {
    return !isEdgenSiteHost(new URL(trimmed).hostname);
  } catch {
    return true;
  }
}

/**
 * App 内置浏览器打开 URL（与 `useBridge().openUrl` 一致）。
 * - App 内：`open-url-inapp`（Android Custom Tab / iOS SFSafariViewController）
 * - 浏览器：`window.open`
 */
export function openInAppBrowserUrl(url: string): void {
  if (typeof window === "undefined" || !url) {
    return;
  }
  const bridge = getBridge();
  if (bridge.isInApp()) {
    bridge.send(MessageType.OPEN_URL_INAPP, { url });
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/** 将相对或绝对 href 转为可在原生 WebView 中加载的绝对 URL。 */
export function resolveAppNavigationUrl(href: string): string {
  if (typeof window === "undefined") {
    return href;
  }
  if (/^https?:\/\//i.test(href)) {
    return href;
  }
  return new URL(href, window.location.origin).href;
}

/**
 * 二级容器（原生 Detail / Chat modal WebView）内，一次交互只允许一次导航。
 *
 * 这里的导航会分流到**两条不同通道**：壳内路径走 bridge（原生收走本容器并在主壳 WebView 加载），
 * 非壳路径走同 WebView `router.push`。同一 tick 连发两次就会两条通道**都执行** —— 容器被交接出去
 * 的同时本文档又漂到了另一个 URL，壳保活复用该 WebView 后表现为「点 A 却进了 B」。
 *
 * 该场景对页面作者不可见（同一个 `router.push` 调用，落哪条通道取决于目标路径），因此在导航层
 * 统一收口，而不是要求每个页面自觉规避。
 *
 * 有意的边界：
 * - 只在 App 的二级容器内生效；主壳 Tab 内的常规 SPA 连续 push / replace 是合法用法，不拦。
 * - 窗口有界（500ms）且在容器重新可见时清零，避免保活 WebView 被永久锁死导航。
 * - 内置浏览器（`open-url-inapp`）不占用也不受限：它不会让本容器消失。
 */
const SHELL_CONTAINER_NAV_WINDOW_MS = 500;
let lastShellContainerNavAtMs = 0;
let shellContainerNavResetInstalled = false;

/**
 * `navigateShellAware` 的重入深度。
 *
 * 调用方传进来的 `router` 可能本身就是 `useShellRouter()` 的壳感知 router —— 它的 `push` / `replace`
 * 会**再次**进入本函数。嵌套那一次是同一个用户意图的延续，不能再占/再判一次导航窗口：否则外层刚占用、
 * 内层立刻被判成「重复导航」丢弃 → 点击**完全没反应**（`ShellLink` 曾因此把 WebView 内所有链接点死）。
 */
let shellContainerNavDepth = 0;

function isInShellSecondaryContainer(): boolean {
  return (
    getBridge().isInApp() &&
    (isInNativeDetailShell() || isInNativeChatShellModal())
  );
}

function ensureShellContainerNavReset(): void {
  if (typeof window === "undefined" || shellContainerNavResetInstalled) {
    return;
  }
  shellContainerNavResetInstalled = true;
  // 保活 WebView 被重新展示（原生复用 / bfcache 恢复）或用户产生新触摸手势时必须解锁，避免合法点击被防抖锁吞掉
  const reset = () => {
    lastShellContainerNavAtMs = 0;
  };
  window.addEventListener("pageshow", reset);
  window.addEventListener("pointerdown", reset, {
    capture: true,
    passive: true,
  });
  window.addEventListener("touchstart", reset, {
    capture: true,
    passive: true,
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      reset();
    }
  });
}

/** 记一次「本容器已发生导航」，不做拦截判断（用于 back / 关闭容器等出口）。 */
function markShellContainerNav(): void {
  if (!isInShellSecondaryContainer()) {
    return;
  }
  ensureShellContainerNavReset();
  lastShellContainerNavAtMs = Date.now();
}

/** 本次导航是否应被忽略；未忽略时占用窗口。 */
function shouldSkipShellContainerNav(href: string): boolean {
  if (!isInShellSecondaryContainer()) {
    return false;
  }
  // 重入（外层已在分流本次导航）→ 不再判定、不再占窗口，见 [shellContainerNavDepth]
  if (shellContainerNavDepth > 1) {
    return false;
  }
  ensureShellContainerNavReset();
  const now = Date.now();
  if (
    lastShellContainerNavAtMs > 0 &&
    now - lastShellContainerNavAtMs < SHELL_CONTAINER_NAV_WINDOW_MS
  ) {
    Track.log(
      "warn",
      "shellAppNavigation",
      "二级容器内已有进行中的导航，忽略本次跳转以避免串页",
      { href },
    );
    return true;
  }
  lastShellContainerNavAtMs = now;
  return false;
}

/** 仅测试用：重置容器导航窗口。 */
export function resetShellContainerNavLatchForTest(): void {
  lastShellContainerNavAtMs = 0;
  shellContainerNavDepth = 0;
}

export type NavigateShellAwareOptions = {
  replace?: boolean;
  /** 透传给 `router.push` / `router.replace` 的第二参数（如 `scroll`） */
  routerOptions?: Parameters<AppRouterInstance["push"]>[1];
};

/**
 * App 内进入非四 Tab 壳路径时走 `openDetailInNative`，否则 `router.push` / `replace`。
 * 组件内优先用 `useShellRouter()`，非 React 场景可继续调用本函数。
 */
export function navigateShellAware(
  router: AppRouterInstance,
  href: string,
  options?: NavigateShellAwareOptions,
): void {
  if (typeof window === "undefined") {
    return;
  }
  shellContainerNavDepth += 1;
  try {
    dispatchShellAwareNavigation(router, href, options);
  } finally {
    shellContainerNavDepth -= 1;
  }
}

function dispatchShellAwareNavigation(
  router: AppRouterInstance,
  href: string,
  options?: NavigateShellAwareOptions,
): void {
  if (getBridge().isInApp() && isInNativeDetailShell()) {
    ensureDetailHistoryTracker();
  }

  // 站外 / mailto / tel：内置浏览器（非 open-detail）
  if (isOffSiteNavigationHref(href)) {
    openInAppBrowserUrl(resolveAppNavigationUrl(href));
    return;
  }

  // Edgen 绝对链（如 beta 域 blog）：与旧版 openUrl 一致
  if (shouldOpenInAppBrowserForHref(href)) {
    openInAppBrowserUrl(resolveAppNavigationUrl(href));
    return;
  }

  // 二级容器内一次交互只允许一次导航（bridge 交接 + 同 WebView SPA 跳转并发 = 串页）
  if (shouldSkipShellContainerNav(href)) {
    return;
  }

  // Detail 内 → 四 Tab 壳路径：关 Detail，在对应 Tab WebView 加载（非 router.push）
  if (shouldPopDetailForTabShellNavigation(href)) {
    getBridge().popDetailAndNavigateShell(resolveAppNavigationUrl(href));
    return;
  }

  // Tab 壳内 → 另一 Tab 的壳路径（或 tabIndex 未知）：切 Tab + loadUrl，避免错 WebView
  if (shouldPopDetailForCrossTabShellNavigation(href)) {
    getBridge().popDetailAndNavigateShell(resolveAppNavigationUrl(href));
    return;
  }

  // Tab 壳内 → 非壳路径：打开原生二级 Detail WebView
  if (shouldUseNativeDetailForHref(href)) {
    openNativeDetailForHref(href);
    return;
  }

  // 本 Tab 壳内 SPA，或已在 Detail 内的非壳互跳：当前 WebView router 导航
  const nav = options?.routerOptions;
  if (options?.replace && router.replace) {
    router.replace(href, nav);
    return;
  }
  router.push(href, nav);
}

/**
 * App 内原生 Detail 二级页：`back` 关闭 WebView；主壳内仍走 `router.back()`。
 */
/** 与 `global.js` 写入的 `mainPath` 对齐：仅比较 origin + pathname，忽略 query/hash。 */
function getPageBackBaselineHref(): string {
  if (typeof window === "undefined") {
    return "";
  }
  return `${window.location.origin}${window.location.pathname}`;
}

/** 是否相对 WebView 首屏 `mainPath` 存在可退路径（不含仅 query 变化）。 */
function canPageBackUsingMainPathBaseline(options?: {
  isGoHome?: boolean;
}): boolean {
  if (typeof window === "undefined" || options?.isGoHome) {
    return false;
  }
  const mainPath = sessionStorage.getItem("mainPath");
  if (!mainPath) {
    return false;
  }
  return mainPath !== getPageBackBaselineHref();
}

/**
 * 页面 Header 返回：App Detail / Chat modal 走 `navigateShellAwareBack`；
 * 其它环境用 `mainPath` 基线判断后 `back` 或 fallback。
 */
export function runShellAwarePageBack(
  router: AppRouterInstance,
  options?: {
    isGoHome?: boolean;
    fallbackHref?: string;
    /**
     * 仅在“没有可回退历史、需要走 fallbackHref”时生效。
     * 适合详情页直达后返回到列表页，避免把详情页继续留在历史栈里。
     */
    fallbackReplaceOnDirectEntry?: boolean;
  },
): void {
  if (typeof window === "undefined") {
    return;
  }

  if (
    getBridge().isInApp() &&
    (isInNativeDetailShell() || isInNativeChatShellModal())
  ) {
    navigateShellAwareBack(router);
    return;
  }

  if (canPageBackUsingMainPathBaseline({ isGoHome: options?.isGoHome })) {
    navigateShellAwareBack(router);
    return;
  }

  const fallbackHref = options?.fallbackHref ?? ROUTER_PATH.HOME;
  if (options?.fallbackReplaceOnDirectEntry && router.replace) {
    router.replace(fallbackHref);
    return;
  }
  router.push(fallbackHref);
}

export function navigateShellAwareBack(router: AppRouterInstance): void {
  if (typeof window === "undefined") {
    return;
  }

  // Chat 底部 modal 壳（Tab 3 WebView）：关闭 overlay，非 history back
  if (getBridge().isInApp() && isInNativeChatShellModal()) {
    // 退出本容器同样占用导航窗口：后续 push 落在正在关闭的 WebView 上就是串页
    markShellContainerNav();
    getBridge().dismissShellChat();
    return;
  }

  // 原生 Detail 二级：优先 Web history back；无可退 history 时再关闭 Detail 容器。
  if (getBridge().isInApp() && isInNativeDetailShell()) {
    if (canGoBackInsideDetailWebView()) {
      markShellContainerNav();
      router.back();
      return;
    }
    if (
      shouldDisableDetailGestureBack(
        normalizePathname(window.location.pathname),
      )
    ) {
      return;
    }
    markShellContainerNav();
    getBridge().closeDetailInNative();
    return;
  }

  // 主 Tab 壳内：welcome / onboarding 沉浸式不可退出
  if (
    getBridge().isInApp() &&
    shouldDisableShellImmersiveBack(normalizePathname(window.location.pathname))
  ) {
    return;
  }

  // 主 Tab 壳内：Next 客户端 history
  router.back();
}
